<template>
  <AppNavbar v-if="!selectorMode" />

  <div :class="['container-xl px-4 clientes-page', selectorMode && hideHeader ? 'mt-2' : 'mt-4']">

    <div v-if="!hideHeader" class="page-toolbar">
      <div>
        <p class="page-eyebrow">Clientes</p>
        <h2>{{ selectorMode ? 'Seleccionar Cliente' : 'Gestión de Clientes' }}</h2>
      </div>
      <div v-if="!selectorMode" class="toolbar-actions">
        <router-link class="btn btn-outline-secondary" :to="{ name: 'ClientesCredito' }">
          <i class="bi bi-wallet2 me-1"></i>Gestión Crédito
        </router-link>
        <button class="btn btn-dark" @click="abrirModalCrear">
          <i class="bi bi-plus-lg me-1"></i>Nuevo Cliente
        </button>
      </div>
    </div>

    <!-- Mensaje inline -->
    <div v-if="notifyMsg" :class="['alert mb-3', notifyType === 'error' ? 'alert-danger' : 'alert-success']" role="alert">
      {{ notifyMsg }}
    </div>

    <!-- Barra de búsqueda y filtros -->
    <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
      <div class="search-box flex-grow-1">
        <i class="bi bi-search"></i>
        <input
          v-model="searchTerm"
          type="search"
          class="form-control"
          placeholder="Buscar por nombre, documento o RUC..."
        />
      </div>
      <div class="d-flex gap-2 flex-shrink-0">
        <button
          v-for="opt in tipoOpts"
          :key="opt.value"
          type="button"
          :class="['btn btn-sm', filtroTipo === opt.value ? 'btn-dark' : 'btn-outline-secondary']"
          @click="setFiltro(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div class="d-flex justify-content-between align-items-center mb-3">
      <span class="text-muted small">{{ total }} cliente(s) encontrado(s)</span>
    </div>

    <!-- Estado vacío / cargando -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>Cargando clientes...
    </div>
    <div v-else-if="!clientes.length" class="text-center py-5 text-muted">
      Sin resultados para los filtros actuales.
    </div>

    <template v-else>
      <!-- Desktop: tabla ≥992px -->
      <div class="d-none d-lg-block">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Nombre</th>
                <th>Documento / RUC</th>
                <th>Teléfono</th>
                <th>Email</th>
                <th>Condición</th>
                <th v-if="!selectorMode" class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in clientes"
                :key="c.cliente_id"
                :class="{ 'table-row-selectable': selectorMode }"
                @click="selectorMode && seleccionarCliente(c)"
              >
                <td class="fw-semibold">{{ c.nombre_completo }}</td>
                <td>{{ c.nro_documento || c.ruc || '—' }}</td>
                <td>{{ c.telefono || '—' }}</td>
                <td>{{ c.email || '—' }}</td>
                <td>
                  <span :class="['badge', c.condiciones_pago === 'CREDITO' ? 'bg-primary' : 'bg-secondary']">
                    {{ c.condiciones_pago || 'CONTADO' }}
                  </span>
                </td>
                <td v-if="!selectorMode" class="text-end">
                  <div class="d-inline-flex gap-2">
                    <button class="btn btn-outline-secondary btn-sm" title="Editar" @click.stop="editarCliente(c)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button class="btn btn-outline-danger btn-sm" title="Eliminar" @click.stop="eliminarCliente(c.cliente_id)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile: cards ≤991.98px -->
      <div class="d-lg-none client-card-list">
        <div
          v-for="c in clientes"
          :key="c.cliente_id"
          class="client-card"
          :class="{ 'client-card--selectable': selectorMode }"
          @click="selectorMode && seleccionarCliente(c)"
        >
          <div class="client-card-main">
            <div class="client-card-info">
              <strong class="d-block">{{ c.nombre_completo }}</strong>
              <small class="text-muted">{{ c.nro_documento || c.ruc || 'Sin documento' }}</small>
            </div>
            <span :class="['badge flex-shrink-0', c.condiciones_pago === 'CREDITO' ? 'bg-primary' : 'bg-secondary']">
              {{ c.condiciones_pago || 'CONTADO' }}
            </span>
          </div>
          <div class="client-card-meta">
            <span v-if="c.telefono"><i class="bi bi-telephone me-1"></i>{{ c.telefono }}</span>
            <span v-if="c.email"><i class="bi bi-envelope me-1"></i>{{ c.email }}</span>
          </div>
          <div v-if="!selectorMode" class="client-card-actions">
            <button class="btn btn-outline-secondary btn-sm" @click.stop="editarCliente(c)">
              <i class="bi bi-pencil me-1"></i>Editar
            </button>
            <button class="btn btn-outline-danger btn-sm" @click.stop="eliminarCliente(c.cliente_id)">
              <i class="bi bi-trash me-1"></i>Eliminar
            </button>
          </div>
        </div>
      </div>
    </template>

    <AppPagination :current-page="page" :total-pages="pages" class="mt-3" @page-changed="onPageChanged" />

    <!-- Modales (no selectorMode) -->
    <RegistrarClienteModal
      v-if="!selectorMode"
      :open="showCrearModal"
      @saved="onClienteCreado"
      @close="showCrearModal = false"
    />
    <RegistrarClienteModal
      v-if="!selectorMode"
      :open="showEditarModal"
      :edit-mode="true"
      :cliente-data="clienteAEditar"
      @saved="onClienteEditado"
      @close="showEditarModal = false"
    />
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, watch, onMounted } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import AppPagination from '@/components/AppPagination.vue'
import RegistrarClienteModal from '@/components/RegistrarClienteModal.vue'
import apiService from '@/services/apiService.js'

defineProps({
  selectorMode: { type: Boolean, default: false },
  hideHeader: { type: Boolean, default: false }
})
const emit = defineEmits(['cliente-seleccionado'])

const clientes = ref([])
const loading = ref(false)
const page = ref(1)
const pages = ref(1)
const total = ref(0)
const limit = 10
const searchTerm = ref('')
const filtroTipo = ref('all')
const notifyMsg = ref('')
const notifyType = ref('ok')
const showCrearModal = ref(false)
const showEditarModal = ref(false)
const clienteAEditar = ref(null)

const tipoOpts = [
  { value: 'all', label: 'Todos' },
  { value: 'CONTADO', label: 'Contado' },
  { value: 'CREDITO', label: 'Crédito' }
]

function notify(msg, type = 'ok') {
  notifyMsg.value = msg
  notifyType.value = type
  setTimeout(() => { if (notifyMsg.value === msg) notifyMsg.value = '' }, 3500)
}

function buildUrl() {
  const p = new URLSearchParams({ page: page.value, limit })
  if (searchTerm.value.trim()) p.set('search', searchTerm.value.trim())
  if (filtroTipo.value !== 'all') p.set('condiciones_pago', filtroTipo.value)
  return `/api/clients?${p.toString()}`
}

async function fetchClientes() {
  loading.value = true
  try {
    const res = await apiService.get(buildUrl())
    const body = res.data || {}
    clientes.value = Array.isArray(body.data) ? body.data : Array.isArray(body) ? body : []
    const pag = body.pagination || {}
    total.value = pag.total || clientes.value.length
    pages.value = pag.totalPages || pag.pages || Math.max(1, Math.ceil(total.value / limit))
  } catch (err) {
    console.error('Error al cargar clientes:', err)
    notify('No se pudieron cargar los clientes. Intente nuevamente.', 'error')
    clientes.value = []
  } finally {
    loading.value = false
  }
}

function setFiltro(tipo) {
  filtroTipo.value = tipo
  page.value = 1
  fetchClientes()
}

function onPageChanged(p) {
  page.value = p
  fetchClientes()
}

let searchDebounce = null
watch(searchTerm, () => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    page.value = 1
    fetchClientes()
  }, 350)
})

function seleccionarCliente(c) {
  emit('cliente-seleccionado', c)
}

function abrirModalCrear() {
  clienteAEditar.value = null
  showCrearModal.value = true
}

function editarCliente(c) {
  clienteAEditar.value = c
  showEditarModal.value = true
}

function onClienteCreado() {
  showCrearModal.value = false
  notify('Cliente registrado correctamente.')
  fetchClientes()
}

function onClienteEditado() {
  showEditarModal.value = false
  clienteAEditar.value = null
  notify('Cliente actualizado correctamente.')
  fetchClientes()
}

async function eliminarCliente(clienteId) {
  const confirmed = await window.confirm('¿Deseas eliminar este cliente? Esta acción no se puede deshacer.')
  if (!confirmed) return
  try {
    await apiService.delete(`/api/clients/${clienteId}`)
    notify('Cliente eliminado correctamente.')
    // Ajusta página si la actual queda vacía
    if (clientes.value.length === 1 && page.value > 1) page.value -= 1
    fetchClientes()
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'Error al eliminar el cliente.'
    notify(msg, 'error')
  }
}

onMounted(fetchClientes)
</script>

<style scoped>
.page-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.page-eyebrow {
  margin: 0;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #6c757d;
}

h2 { margin: 0; }

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: .75rem;
  flex-shrink: 0;
}

.search-box {
  position: relative;
  min-width: 220px;
}

.search-box i {
  position: absolute;
  top: 50%;
  left: .75rem;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
}

.search-box .form-control {
  padding-left: 2.25rem;
}

.table-row-selectable {
  cursor: pointer;
}

.table-row-selectable:hover td {
  background-color: #f0f4ff;
}

/* Mobile cards */
.client-card-list { display: flex; flex-direction: column; gap: .6rem; }

.client-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: .5rem;
  padding: .9rem 1rem;
}

.client-card--selectable { cursor: pointer; }
.client-card--selectable:active { background: #f8f9fa; }

.client-card-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: .75rem;
  margin-bottom: .4rem;
}

.client-card-info { flex: 1; min-width: 0; }

.client-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem .9rem;
  color: #6c757d;
  font-size: .84rem;
  margin-bottom: .6rem;
}

.client-card-actions {
  display: flex;
  gap: .5rem;
}

@media (max-width: 991.98px) {
  .page-toolbar { flex-direction: column; align-items: flex-start; }
  .toolbar-actions { flex-wrap: wrap; }
}
</style>
